import type { TCacheEntry } from '@casumo/fabric-fundamentals';
import type { BaseCache } from '$lib/services/cache/BaseCache';

type TSWRFetch = {
    fetcher: () => Promise<Response>;
    cache: BaseCache;
    key?: string;
    ttl?: number;
    freshness?: number;
    tags?: string[];
    bypassCache?: boolean;
};

export async function swrFetch({
    fetcher,
    cache,
    key,
    ttl,
    freshness,
    tags,
    bypassCache = false
}: TSWRFetch): Promise<Response> {
    if (!key || bypassCache) {
        return fetcher();
    }

    // Try to get cached entry
    let cacheEntry: TCacheEntry | undefined;
    let request: Promise<Response> | undefined;
    if (await cache.has(key)) {
        cacheEntry = await cache.get(key)!;

        if (shouldFetchInTheBackground(cacheEntry!)) {
            request = fetcher();
            cache.set(key, request, { ttl, freshness, tags });
        }

        return Promise.resolve(createResponse(cacheEntry!.data));
    }

    request = fetcher();
    cache.set(key, request, { ttl, freshness, tags });
    return request;
}

export function shouldFetchInTheBackground(cacheEntry: TCacheEntry): boolean {
    return Boolean(cacheEntry.freshUntil) && Date.now() > cacheEntry.freshUntil!;
}

export function createResponse(data: unknown): Response {
    return new Response(typeof data === 'string' ? data : JSON.stringify(data), {
        status: 200,
        headers: new Headers({
            'Content-Type': 'application/json'
        })
    });
}
